import './index.scss';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Button, Iconfont, Modal, UmuSelect, Tooltip, Switch } from '@umu/umu-design';
import { ModalFuncProps } from '@umu/design-base';
import { lang } from '@umu/ukit-lang';
import {
    IClosePermissionInfo,
    IPermissionSettingDataInfo,
    IPermissionSettingProps,
    PermissionType,
    PlatePermissionType,
} from '../../interface';
import {
    defaultContentLinkInfo,
    defaultNavLinkInfo,
    defaultPermissionDataInfo,
    defaultWhiteOverlayInnerStyle,
    shareOptionMap,
} from '../../dataFlow';
import { If, Loading, IfShow } from 'common/src/components';
import { getPermissionList } from '@umu/umu-api';
import { ISearchItem, SearchInput } from '../../../searchInput';
import { PermissionMemberList } from '../memberList';
import { PermissionContentInfo } from '../permissionContentInfo';
import { onTransformParams } from '../permissionContentInfo/dataFlow';

import {
    getSearchAccountByKeyword,
    setSeNavPermission,
    setSeAccountPermission,
    getNavBasicInfoById,
    getNavUsePlateMemberList,
    setCancelPermissionAll,
} from '../../server';
import { onTransformMenuList } from '../memberInfo';
import { IAccountInfo } from '../../interface/account';

const { confirm } = Modal;
const getTips = (list: JSX.Element | string | string[] | JSX.Element[]) => {
    const newList = (Array.isArray(list) ? list : [list]).filter((_) => !!_);
    return newList.length > 0 ? (
        <Fragment>
            {newList.map((_, index) => {
                return <p key={`tips-div-${index}`}>{_}</p>;
            })}
        </Fragment>
    ) : (
        ''
    );
};

const PermissionSettingPage = (props: IPermissionSettingProps) => {
    const {
        prefixCls = 'global-permission-setting-page',
        okText = '',
        isCloseInitLoading = false,
        suffixElement,
        prefixElement,
        isHideContentLink = false,
        cancelText = '',
        okProps = {},
        platformPermission,
        contentLinkInfo = defaultContentLinkInfo,
        isHideFooter = false,
        params,
        onClose,
        onSuccess,
        normalizeAccountEPU,
        normalizeAccountName,
    } = props;
    const [isLoad, setIsLoad] = useState(true);
    const [dataInfo, setDataInfo] = useState<IPermissionSettingDataInfo>(defaultPermissionDataInfo);
    const [closeInfo, setCloseInfo] = useState<IClosePermissionInfo>({
        isAll: false,
        list: [],
    });
    const [accountSelectList, setAccountSelectList] = useState<ISearchItem[]>([]);

    const isLevelFirst = useMemo(() => {
        return Number(dataInfo.navInfo.level) === 1;
    }, [dataInfo.navInfo.level]);

    const onUpdateDataInfo = (data: IPermissionSettingDataInfo) => {
        setDataInfo({
            ...dataInfo,
            ...data,
        });
    };
    const initDataList = () => {
        Promise.all([
            getPermissionList({
                obj_id: params.id,
                obj_type: params.obj_type,
                se_id: params?.se_id || '',
            }),
            getNavBasicInfoById({
                id: params?.id || '',
                se_id: params?.se_id || '',
            }),
        ]).then(([res, info]) => {
            const { list = [], selected } = res;
            const isUseAndUmuPlate = Number(platformPermission) === PlatePermissionType.UMU_AND_USE;
            const permissionList = list.map((item) => {
                let label = shareOptionMap[(item as PermissionType) || PermissionType.__INIT__].label;
                if (isUseAndUmuPlate && String(item) === PermissionType.PUBLIC_IN_THE_COMPANY) {
                    label = shareOptionMap[PermissionType.USE_PLATE].label;
                }
                return {
                    label,
                    value: item,
                };
            });

            onUpdateDataInfo({
                ...dataInfo,
                permissionList,
                isLoading: false,
                permissionValue: selected as PermissionType,
                navInfo: {
                    ...info,
                    is_open: info.is_open || 0,
                    old_is_open: info.is_open || 0,
                },
            });
            isLoad && setIsLoad(false);
        });
    };

    // 保存按钮文字信息
    const okTextName = useMemo(() => {
        return okText || lang('完成');
    }, [okText]);

    // 取消按钮文字
    const cancelTextName = useMemo(() => {
        return cancelText || lang('取消');
    }, [cancelText]);

    const onSubmit = async () => {
        if (!isLevelFirst) {
            const isOpen = dataInfo.navInfo.is_open;
            const oldIsOpen = dataInfo.navInfo.old_is_open;
            if (isOpen !== oldIsOpen) {
                setSeNavPermission({
                    id: params?.id || '',
                    se_id: params?.se_id || '',
                    is_open: isOpen,
                }).then((res) => {
                    onSuccess?.({ ...res, info: dataInfo });
                    onClose?.();
                });
            } else {
                onClose?.();
            }
            return;
        }
        const accountList = accountSelectList || [];
        const permissionValueChange = dataInfo.permissionValue !== dataInfo.navInfo.access_permission;
        const isClearPermission = dataInfo.permissionValue === PermissionType.DESIGNATED_ACCOUNT && closeInfo.isAll;
        const isAccountChange =
            dataInfo.permissionValue === PermissionType.DESIGNATED_ACCOUNT &&
            (accountList.length > 0 || closeInfo.list.length > 0);
        if (permissionValueChange) {
            const newParams = {
                id: params?.id || '',
                se_id: params?.se_id || '',
                access_permission: dataInfo.permissionValue,
            };
            const res = await setSeNavPermission(newParams);
            onSuccess?.({ ...res, info: dataInfo });
        }
        if (isClearPermission) {
            await setCancelPermissionAll({
                obj_id: params?.id || '',
                obj_type: 'se_custom_nav',
            });
        }
        if (isAccountChange) {
            const list = onTransformParams(accountList, closeInfo.list);
            await setSeAccountPermission({
                obj_id: params?.id || '',
                obj_type: 'se_custom_nav',
                se_id: params?.se_id || '',
                accounts: JSON.stringify(list),
            });
        }
        onClose?.();
    };
    const onNavSwitchChange = (checked: boolean) => {
        setDataInfo({
            ...dataInfo,
            navInfo: {
                ...dataInfo.navInfo,
                is_open: checked ? 1 : 0,
            },
        });
    };
    const onEditorRootPermission = () => {
        onClose?.();
        onDialogPermissionSettingPage({
            normalizeAccountEPU,
            normalizeAccountName,
            title: lang('权限设置'),
            params: {
                id: dataInfo.navInfo.root_id,
                se_id: dataInfo.navInfo.se_id,
                obj_type: 'se_custom_nav',
            },
            onClose: () => {
                if (isLevelFirst) {
                    onDialogPermissionSettingPage(props);
                }
            },
            onSuccess: (data) => {
                data && data?.isSuccess && onDialogPermissionSettingPage(props);
            },
        });
    };
    useEffect(() => {
        if (!isCloseInitLoading) {
            initDataList();
        } else {
            setIsLoad(false);
        }
    }, []);
    const onPermissionChange = (item: any) => {
        onUpdateDataInfo({
            ...dataInfo,
            permissionValue: item as PermissionType,
        });
    };
    const onLoadSearchData = (value: string) => {
        if (value.trim() === '') {
            return Promise.resolve([]);
        }
        return getSearchAccountByKeyword({
            search_source: 'access_permission',
            is_suggestion: '1', // 模糊搜索
            nav_id: params.id,
            se_id: params.se_id,
            accounts: value,
        }).then((res) => {
            return res;
        });
    };
    const onPreviewLoadData = (value: string) => {
        if (value.trim() === '') {
            return Promise.resolve([]);
        }
        return getSearchAccountByKeyword({
            search_source: 'access_permission',
            is_suggestion: '1', // 模糊搜索
            nav_id: params.id,
            se_id: params.se_id,
            accounts: value,
        }).then((res) => {
            return onTransformMenuList(res as any[]);
        });
    };
    const onUserListChange = (data: ISearchItem[], _isDel?: boolean) => {
        setAccountSelectList(data);
    };

    const onCancelPermissionAll = () => {
        setCloseInfo({
            isAll: true,
            list: [],
        });
    };
    const onCancelPermission = (item: IAccountInfo) => {
        setCloseInfo({
            isAll: false,
            list: [...closeInfo.list, item],
        });
    };

    const renderFirstLevel = () => {
        return (
            <div className={`${prefixCls}-content-permission`}>
                <If condition={dataInfo.permissionList.length > 0}>
                    <UmuSelect
                        width="100%"
                        value={dataInfo.permissionValue}
                        className={`${prefixCls}-content-permission-list`}
                        dropdownClassName={`${prefixCls}-content-permission-dropdown`}
                        options={dataInfo.permissionList}
                        onChange={onPermissionChange}
                    />
                </If>
                <IfShow show={dataInfo.permissionValue === PermissionType.DESIGNATED_ACCOUNT}>
                    <Fragment>
                        <div className={`${prefixCls}-content-permission-search-member`}>
                            <SearchInput
                                preTitle={lang('以下账户可见：')}
                                isSePlate
                                useId={params?.se_id || ''}
                                className={`${prefixCls}-permission-search`}
                                placeholder={lang('请输入账户名或部门名，用逗号分隔')}
                                isAllowShowBatch
                                isAllowShowDepartment
                                batchProps={{
                                    previewPlaceholder: lang(
                                        '请输入成员账户信息（邮箱、手机号、用户名）、部门名，用逗号或回车分隔，输入完成后点击下方“预览”按钮查看。',
                                    ),
                                }}
                                onTransformMenuList={onTransformMenuList as any}
                                onPreviewLoadData={onPreviewLoadData}
                                onLoadData={onLoadSearchData}
                                onSelectChange={onUserListChange}
                            />
                        </div>
                        <div className={`${prefixCls}-content-permission-member-list`}>
                            <PermissionMemberList
                                id={params?.id || ''}
                                isModeView={
                                    (String(dataInfo.permissionValue) as PermissionType) ===
                                    PermissionType.PUBLIC_IN_THE_COMPANY
                                }
                                seId={params?.se_id || ''}
                                sourceType="se_custom_nav"
                                closeList={closeInfo.list}
                                normalizeAccountEPU={normalizeAccountEPU}
                                normalizeAccountName={normalizeAccountName}
                                onCancelPermission={onCancelPermission}
                                onCancelPermissionAll={onCancelPermissionAll}
                            />
                        </div>
                    </Fragment>
                </IfShow>
                <IfShow show={dataInfo.permissionValue === PermissionType.USE_PLATE}>
                    <div
                        className={`${prefixCls}-content-permission-member-list ${prefixCls}-content-permission-rows-split`}
                    >
                        <PermissionMemberList
                            id={params?.id || ''}
                            isModeView={
                                (String(dataInfo.permissionValue) as PermissionType) ===
                                PermissionType.PUBLIC_IN_THE_COMPANY
                            }
                            seId={params?.se_id || ''}
                            sourceType="se_custom_nav"
                            onLoadData={(params) =>
                                getNavUsePlateMemberList({
                                    se_id: params.se_id,
                                    page: params.page,
                                    size: params.size,
                                })
                            }
                            closeList={closeInfo.list}
                            normalizeAccountEPU={normalizeAccountEPU}
                            normalizeAccountName={normalizeAccountName}
                        />
                    </div>
                </IfShow>
            </div>
        );
    };

    const renderNotFirstLevel = () => {
        return (
            <div className={`${prefixCls}-content-permission`}>
                <div className={`${prefixCls}-sub-title`}>
                    {lang('二级及以下导航的权限，与所属一级导航权限相同；在权限范围内，可以设置当前导航公开或关闭。')}
                </div>
                <div className={`${prefixCls}-content-switch`}>
                    <span className={`${prefixCls}-content-switch-label`}>{lang('公开当前导航')}</span>
                    <Switch defaultChecked={Number(dataInfo?.navInfo?.is_open) === 1} onChange={onNavSwitchChange} />
                </div>
                <div className={`${prefixCls}-rows-line`} />
                <div className={`${prefixCls}-content-roots-info`}>
                    <div className={`${prefixCls}-content-roots-info-title`}>{lang('所属一级导航权限')}</div>
                    <div className={`${prefixCls}-content-roots-info-desc`}>
                        <span className={`${prefixCls}-content-roots-info-desc-label`}>
                            {shareOptionMap?.[
                                (dataInfo.navInfo?.root_access_permission as PermissionType) || PermissionType.__INIT__
                            ]?.label || lang('暂未配置')}
                        </span>
                        <Button type="text" isOnlyChildren onClick={onEditorRootPermission}>
                            {lang('去编辑')}
                        </Button>
                    </div>
                    <If
                        condition={
                            String(dataInfo.navInfo?.root_access_permission) === PermissionType.DESIGNATED_ACCOUNT
                        }
                    >
                        <Fragment>
                            <PermissionMemberList
                                id={dataInfo.navInfo?.root_id || ''}
                                seId={params?.se_id || ''}
                                sourceType="se_custom_nav"
                                closeList={closeInfo.list}
                                onCancelPermission={onCancelPermission}
                                onCancelPermissionAll={onCancelPermissionAll}
                                normalizeAccountEPU={normalizeAccountEPU}
                                normalizeAccountName={normalizeAccountName}
                            />
                        </Fragment>
                    </If>
                </div>
            </div>
        );
    };
    const renderNavContent = () => {
        if (dataInfo.isLoading) {
            return null;
        }
        return (
            <div className={`${prefixCls}-content`}>
                <div className={`${prefixCls}-content-heard`}>{defaultNavLinkInfo?.title}</div>
                {isLevelFirst ? renderFirstLevel() : renderNotFirstLevel()}
            </div>
        );
    };

    const renderPermissionContent = () => {
        if (dataInfo.isLoading) {
            return null;
        }
        return (
            <div className={`${prefixCls}-content`}>
                <If condition={!isHideContentLink}>
                    <div className={`${prefixCls}-link-content`}>
                        <Tooltip
                            title={getTips(contentLinkInfo.tips || '')}
                            placement="bottom"
                            color="#ffffff"
                            zIndex={3100}
                            overlayInnerStyle={defaultWhiteOverlayInnerStyle}
                        >
                            <div className={`${prefixCls}-link-content-heard`}>
                                {contentLinkInfo?.title}
                                {contentLinkInfo.tips && <Iconfont type="Tips" className="content-tip-icon" />}
                            </div>
                        </Tooltip>
                        <div className={`${prefixCls}-link-content-body`}>
                            <div className={`${prefixCls}-sub-title`}>{contentLinkInfo?.content}</div>
                            <div className={`${prefixCls}-normal-title ${prefixCls}-normal-row-split`}>
                                <PermissionContentInfo
                                    dataInfo={dataInfo}
                                    accountList={accountSelectList}
                                    closeInfo={closeInfo}
                                    normalizeAccountEPU={normalizeAccountEPU}
                                    normalizeAccountName={normalizeAccountName}
                                />
                            </div>
                        </div>
                    </div>
                </If>
            </div>
        );
    };
    return (
        <div className={`${prefixCls}`}>
            <If condition={!!prefixElement}>
                <div className={`${prefixCls}-heard`}>{prefixElement}</div>
            </If>
            <If condition={dataInfo.isLoading}>
                <Loading />
            </If>
            {renderNavContent()}
            {renderPermissionContent()}
            <If condition={!!suffixElement}>
                <div className={`${prefixCls}-suffix`}>{suffixElement}</div>
            </If>
            <If condition={!isHideFooter}>
                <div className={`${prefixCls}-footer`}>
                    <Button type="ghost-grey" className={`${prefixCls}-footer-col-split`} onClick={onClose}>
                        {cancelTextName}
                    </Button>
                    <Button type="primary-blue" onClick={onSubmit} {...okProps}>
                        {okTextName}
                    </Button>
                </div>
            </If>
        </div>
    );
};

/**
 * @desc 微页面配置信息弹窗页面 打开弹窗函数
 */
const onDialogPermissionSettingPage = (data: IPermissionSettingProps, dialogInfo?: ModalFuncProps) => {
    const { title = lang('权限设置'), ...rest } = dialogInfo || {};

    const dialog = confirm({
        title,
        centered: true,
        width: '8.6rem',
        footer: null,
        zIndex: 1000,
        className: 'global-permission-setting-page-modal',
        ...rest,
        content: (
            <PermissionSettingPage
                {...data}
                onClose={() => {
                    data?.onClose?.();
                    dialog.destroy();
                }}
            />
        ),
    } as any);
    return dialog;
};

export { PermissionSettingPage, onDialogPermissionSettingPage };
